import { IntlShape } from 'react-intl'

import currency from 'helpers/currency'
import { OfferItem } from 'generated/types'
import { RemoteLineItem, SanitizedLineItem } from 'components/OfferForm/types'

export const calculateLineItemPrice = (
  { price, quantity }: Partial<OfferItem>,
  intl: IntlShape,
) => currency(price, intl).multiply(quantity || 1)

export const formattedLineItemPrice = (
  item: OfferItem | RemoteLineItem | SanitizedLineItem,
  intl: IntlShape,
  isGross: boolean | undefined,
) => (
  item.price ? (
    intl.formatNumber(
      sanitizeLineItemPrices(
        { ...item, price: Number(item.price) },
        intl,
        { convertPricesToGross: isGross || undefined },
      ).value,
      { minimumFractionDigits: 2, style: 'decimal' },
    )
  ) : '0,00'
)

export const sanitizeLineItemPrices = (
  { price, taxRate }: Partial<OfferItem>,
  intl: IntlShape,
  { isOfferGross = false, convertPricesToGross = false },
) => {
  if (isOfferGross) {
    return grossToNet({ price, taxRate }, intl)
  } else if (convertPricesToGross) {
    return netToGross({ price, taxRate }, intl)
  } else {
    return currency(price, intl)
  }
}

export const lineItemsSummary = (
  { items, guestCount }: { items: Array<Partial<OfferItem>>, guestCount?: number | null },
  isGross = false, intl: IntlShape,
) => (
  items.filter((item) => !item.optional).reduce((summary, item) => {
    const taxRate = Number(item?.taxRate)
    const price = parseFloat(item.price || 0)

    let gross
    let net

    if (isGross) {
      gross = calculateLineItemPrice({ price, quantity: item.quantity }, intl)
      net = grossToNet({ price: gross.value, taxRate }, intl)
    } else {
      net = calculateLineItemPrice({ price, quantity: item.quantity }, intl)
      gross = netToGross({ price: net.value, taxRate }, intl)
    }

    const tax = gross.subtract(net)

    summary.net += net.value
    summary.perPerson += guestCount ? net.divide(guestCount).value : 0
    summary.gross += gross.value

    if (item && !item.inclusive && item.taxRate !== '') {
      summary.taxRates[taxRate] = (summary.taxRates?.[taxRate] || 0) + tax.value
    }

    return summary
  }, { net: 0, perPerson: 0, gross: 0, taxRates: {} as { [rate: string]: number } })
)

const netToGross = (
  { price, taxRate }: Partial<OfferItem>,
  intl: IntlShape,
) => currency(price, intl).multiply((100 + Number(taxRate)) / 100)
const grossToNet = (
  { price, taxRate }: Partial<OfferItem>,
  intl: IntlShape,
) => currency(price, intl).multiply(100 / (100 + Number(taxRate)))

export const OFFER_PRICE_TYPE_VALUE_MAP = [null, false, true]
